import * as Styled from '../UploadEventPhotoModal/UploadEventPhotoModal.styles';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  IconButton,
} from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import BaseModal from "../BaseModal/BaseModal";
import { ChipListInput, Input } from "../../components";
import { POST_EVENT_IMAGE } from "../../services/endpoints";
import axiosAPI from "../../services/axios";
import imageCompression from "browser-image-compression";
import { refreshLiveEventPhotos } from "../../store/actions/liveEventActions";
import { toBase64 } from "../../utils/utils";
import { useAppDispatch } from "../../store/hooks";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useTheme } from "@mui/material";
import { v4 as uuid } from "uuid";
import AddPhotoButton from '../../components/AddPhotoButton/AddPhotoButton';
import EditUploadPhotoModal from '../EditUploadPhotoModal/EditUploadPhotoModal';
import { CustomSlider } from '../UploadEventPhotoModal/UploadEventPhotoModal.styles';

type UploadEventPhotoModalProps = {
  open: any;
  onClose: any;
  selectedImgArray: string[];
  eventId?: any;
  uploadEventPhotoFile?: File | null;
  uploadEventPhotoFileArray: File[];
  deletePhotos: (index?: number) => void;
  handleFileChange: (event: any) => void;
  handleCaptionChange: (photoIndex: number, caption: string) => void;
  uploadCaptionsArray: string[];
};

type UploadEventPhotoForm = {
  tags: string[];
  captions: { caption: string }[];
};

const UploadEventPhotoModal = ({
  open,
  onClose,
  selectedImgArray,
  eventId,
  uploadEventPhotoFileArray,
  handleFileChange,
  deletePhotos,
  handleCaptionChange,
  uploadCaptionsArray,
}: UploadEventPhotoModalProps) => {
  const theme = useTheme();
  const user = useSelector((state: any) => state.user);
  const [errorMessage, setErrorMessage] = useState("");
  const [isValidFile, setIsValidFile] = useState(false);
  const [hovered, setHovered] = useState(false);

  const [showNoPhotoSelectedModal, setShowNoPhotoSelectedModal] =
    useState(false);
  const [
    showEditUploadPhotoSelectedModal,
    setShowEditUploadPhotoSelectedModal,
  ] = useState(-1);

  const formMethods = useForm({
    defaultValues: {
      tags: [],
      captions: [{ caption: '' }],
    },
    mode: 'onChange',
  });

  const dispatch = useAppDispatch();
  const [photosUploadedCount, setPhotosUploadedCount] = useState(0);
  const [totalPhotos, setTotalPhotos] = useState(
    uploadEventPhotoFileArray.length
  );
  const [uploadPhotoLoading, setUploadPhotoLoading] = useState(false);

  var slideShowSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1028,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
          infinite: false,
          dots: true
        }
      },
      {
        breakpoint: 770,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]
  };

  const handleDeletePhotos = (index?: number) => {
    deletePhotos(index);
    setErrorMessage("")
    if (index === undefined || !selectedImgArray.length) {
      setShowNoPhotoSelectedModal(true); // Show "No photos selected" if no photos are left
    } else {
      setShowNoPhotoSelectedModal(false); // Hide the message if there are photos
    }
    formMethods.reset();
  };

  const handleFileChangeAndClose = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setShowNoPhotoSelectedModal(false);
    handleFileChange(event);
  };

  const uploadImage = async (file: File | undefined) => {
    const options = {
      maxSizeMB: 3.0, // (max file size in MB)
      maxWidthOrHeight: 1920, // compress the image to this maximum width or height
      useWebWorker: true,
    };
    if (!file) return '';
    try {
      const compressedFile = await imageCompression(file, options);
      return await toBase64(compressedFile);
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error; // Throw the error so it can be caught in the parent scope
    }
  };

  const postEventImage = async (eventId: string, photoUpload: any) => {
    if (typeof eventId !== 'string') return;
    try {
      await axiosAPI.post(POST_EVENT_IMAGE(eventId), photoUpload);
      dispatch(refreshLiveEventPhotos(eventId));
    } catch (error) {
      console.error('Error posting event image:', error);
      throw error; // Throw the error so it can be caught in the parent scope
    }
  };

  const validateFileFormat = (file: File) => {
    const allowedExtensionsRegx = /(\.jpg|\.jpeg|\.png)$/i;
    return allowedExtensionsRegx.test(file.name.substring(file.name.lastIndexOf(".")))
  }

  const onCloseClearErrors = () => {
    setErrorMessage('');
    formMethods.reset();
    onClose();
  };

  const onSubmit: SubmitHandler<UploadEventPhotoForm> = async (data) => {
    setErrorMessage('');
    setUploadPhotoLoading(true);
    setPhotosUploadedCount(0); // Reset the counter
    setTotalPhotos(uploadEventPhotoFileArray.length);
    try {
      if (uploadEventPhotoFileArray.length && isValidFile) {
        for (let i = 0; i < uploadEventPhotoFileArray.length; i++) {
          const uploadEventPhotoFile = uploadEventPhotoFileArray[i];
          const base64Image = await uploadImage(uploadEventPhotoFile);
          const newPhotoUpload = {
            name: `live-${eventId}-${uuid()}$${uploadEventPhotoFile?.name}`,
            data: base64Image,
            uploaderName: user.name,
            uploaderEmail: user.email,
            caption: data.captions[i]?.caption?.trim() || uploadCaptionsArray[i] || '',
          };
          await postEventImage(eventId, newPhotoUpload);
          // Update the count of uploaded photos
          setPhotosUploadedCount((prev) => prev + 1);
        }

        onClose();
        formMethods.reset();
      }
      else {
        if (!isValidFile) {
          setErrorMessage("One or more files are not supported. Please upload supported file type.");
        }
      }
    } catch (error) {
      setErrorMessage('Upload failed. Please try again.');
    } finally {
      setUploadPhotoLoading(false);
    }
  };

  useEffect(() => {
    if (uploadEventPhotoFileArray.length) {
      uploadEventPhotoFileArray.forEach((file) => {
        setIsValidFile(validateFileFormat(file));
      })
    }
  }, [uploadEventPhotoFileArray, uploadEventPhotoFileArray.length])


  return (
    <>
      <BaseModal
        open={open}
        onClose={onCloseClearErrors}
        tabletWidth="80%"
        desktopWidth="70%"
        fullScreenMobile
        overflow="auto"
      >
        <Stack
          direction="column"
          width="100%"
          style={{ marginTop: theme.spacing(1) }}
        >
          <Box>
            <FormProvider {...formMethods}>
              <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                <Styled.UploadPhotoHeaderText variant="h1">
                  Upload preview
                </Styled.UploadPhotoHeaderText>
                {selectedImgArray.length > 1 ? (
                  <Styled.UploadPhotoSubText variant="modalBody">
                    Select a photo to add a caption or tag a team member. After
                    upload, photo(s) will be viewable by all attendees on the
                    “Photos” tab of the live event space.
                  </Styled.UploadPhotoSubText>
                ) : (
                  <Styled.UploadPhotoSubText variant="modalBody">
                    After upload, photo will be viewable by all attendees on the
                    “Photos” tab of the live event space.
                  </Styled.UploadPhotoSubText>
                )}
                <Styled.UploadPhotoSubText variant="modalBody">
                  {selectedImgArray.length >= 1 ? (
                    <>
                      <span style={{ fontFamily: 'Slalom Sans Bold' }}>
                        Reminder:{' '}
                      </span>{' '}
                      If you plan to upload or appear in event photos please
                      fill out this
                      <span
                        style={{
                          color: theme.colors.primary.slalomBlue,
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          transition: 'opacity 0.3s ease',
                          opacity: hovered ? 0.7 : 1,
                        }}
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                        onClick={() => {
                          window.open(
                            'https://slalom1.na1.echosign.com/public/esignWidget?wid=CBFCIBAA3AAABLblqZhAQGijvLN7znz6NyODCVKpcLGqdP-FohfL3JzXnLpRcfJmbmeZUdWU2iQ4JEN9x4Bk',
                            '_blank'
                          );
                        }}
                      >
                        Photo Release Waiver
                      </span>
                      {`. For the “Subject matter of Media” part of the form, please
                enter {x}.`}{' '}
                    </>
                  ) : null}
                </Styled.UploadPhotoSubText>
                {selectedImgArray.length >= 1 && !showNoPhotoSelectedModal ? (
                  <>
                    {selectedImgArray.length > 1 ? (
                      <>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          style={{
                            marginBottom: theme.spacing(3),
                          }}
                        >
                          <Typography variant="modalSubtitle">
                            Photos selected ({selectedImgArray.length})
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 0.7,
                              },
                            }}
                            onClick={() => handleDeletePhotos()}
                          >
                            <Typography
                              style={{
                                marginRight: theme.spacing(1),
                              }}
                              variant="body2"
                            >
                              Remove all photos
                            </Typography>
                            <Styled.RemoveIcon />
                          </Box>
                        </Box>
                        <CustomSlider {...slideShowSettings}>
                          {selectedImgArray
                            .map((selectedImg, i) => (
                              <>
                                <Styled.MultiSelectedImage
                                  onClick={() =>
                                    setShowEditUploadPhotoSelectedModal(i)
                                  }
                                  src={selectedImg}
                                  alt=""
                                />
                                <Box alignItems="center" display="flex" height="100%" width="auto">
                                  <Typography
                                    marginRight="8px"
                                    variant="modalBody"
                                  >
                                    {uploadEventPhotoFileArray[i].name}
                                  </Typography>
                                  <IconButton
                                    disableRipple
                                    onClick={() => handleDeletePhotos(i)}
                                  >
                                    <Styled.RemoveIcon />
                                  </IconButton>
                                </Box>
                              </>
                            ))}
                        </CustomSlider>

                        <Divider
                          sx={{
                            width: '100%',
                            marginTop: theme.spacing(3),
                            marginBottom: theme.spacing(3),
                            [theme.breakpoints.down('tablet')]: {
                              marginTop: theme.spacing(20),
                            },
                          }}
                        ></Divider>
                      </>
                    ) : (
                      <>
                        {selectedImgArray.map((selectedImg, i) => (
                          <Box key={`selectedImg-${i}`}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              style={{
                                marginBottom: theme.spacing(3),
                              }}
                            >
                              <Typography variant="modalSubtitle">
                                {uploadEventPhotoFileArray[i].name}
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    opacity: 0.7,
                                  },
                                }}
                                onClick={() => handleDeletePhotos(i)}
                              >
                                <Typography
                                  style={{
                                    marginRight: theme.spacing(1),
                                  }}
                                  variant="body2"
                                >
                                  Remove Photo
                                </Typography>
                                <Styled.RemoveIcon />
                              </Box>
                            </Box>
                            <Styled.SelectedImage src={selectedImg} alt="" />
                            <Input
                              name={`captions[${i}].caption`}
                              key={`caption-${i}`}
                              control={formMethods.control}
                              label={'Photo Caption'}
                              placeholder="Enter caption..."
                              fullWidth
                            />
                            <ChipListInput
                              name={'tags'}
                              control={formMethods.control}
                              label={'Tags'}
                              placeholder="Start typing a name..."
                              fullWidth
                            />
                          </Box>
                        ))}
                      </>
                    )}
                  </>
                ) : (
                  <Styled.InnerContainer>
                    <Box>
                      <Styled.InnerContainerItems>
                        <Typography marginBottom={3}>
                          No photos selected
                        </Typography>
                        <AddPhotoButton
                          textWeight={700}
                          variant="contained"
                          disabled={false}
                          showIcon={false}
                          textColor="white"
                          handleFileChange={handleFileChangeAndClose}
                        />
                      </Styled.InnerContainerItems>
                    </Box>
                  </Styled.InnerContainer>
                )}

                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    paddingBottom: theme.spacing(1),
                    [theme.breakpoints.down('tablet')]: {
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    },
                  }}
                >
                  {uploadPhotoLoading ? (
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <CircularProgressWithLabel
                        value={photosUploadedCount}
                        total={totalPhotos}
                      />
                    </Box>
                  ) : selectedImgArray.length >= 1 ? (
                    <>
                      <AddPhotoButton
                        variant="none"
                        disabled={false}
                        handleFileChange={handleFileChange}
                      />
                      <Button
                        variant="contained"
                        type="submit"
                        disabled={!!errorMessage}
                        sx={{
                          [theme.breakpoints.down('tablet')]: {
                            width: '100%',
                          },
                        }}
                      >
                        Upload
                      </Button>
                    </>
                  ) : null}
                </Box>
                {errorMessage && (
                  <Typography sx={{ display: "flex", order: 1, justifyContent: "flex-end" }} color="error">{errorMessage}</Typography>
                )}
              </form>
            </FormProvider>
          </Box>
        </Stack>
      </BaseModal>
      {showEditUploadPhotoSelectedModal >= 0 && (
        <EditUploadPhotoModal
          open={setShowEditUploadPhotoSelectedModal}
          image={selectedImgArray[showEditUploadPhotoSelectedModal]}
          imageName={
            uploadEventPhotoFileArray[showEditUploadPhotoSelectedModal].name
          }
          photoIndex={showEditUploadPhotoSelectedModal}
          handleCaptionChange={handleCaptionChange}
          onClose={() => {
            setShowEditUploadPhotoSelectedModal(-1);
          }}
        />
      )}
    </>
  );
};

export default UploadEventPhotoModal;

// Function to display CircularProgress with a label
function CircularProgressWithLabel({
  value,
  total,
}: {
  value: number;
  total: number;
}) {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress variant="determinate" value={(value / total) * 100} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" component="div" color="text.secondary">
          {`${value} / ${total}`}
        </Typography>
      </Box>
    </Box>
  );
}
